/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { I18N } from 'aurelia-i18n';
import { AuthWithLocalState } from 'app/um/models/local-state-extensions';
import { IApiSettings, IApiResults } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { apiCall } from 'app/shared/lib/api/api';
import { Toaster } from 'app/shared/lib/toaster';
import { AuthCorrespondenceEvent, AuthAutoDeterminationEvent } from 'app/um/lib/events';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { authIsReadOnly, getProviderContractStatus } from 'app/um/lib/utilities';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { requiredFields as cwqiGuidelineFields } from 'app/ext/cwqi/components/cwqi-episode-guideline-grid';
import { CwqiEpisodeGuideline } from 'app/mcg/models/essette-api';
import { CWQIService } from 'app/mcg/services/cwqi-service';
import { CwqiReloadedGuidelinesEvent } from 'app/ext/cwqi/cwqi-manager';
import { AccountContext } from 'app/core/lib/account-context';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import * as UM from 'app/um/models/essette-api';
import { deepClone } from 'app/shared/lib/utilities';

export interface IAuthContextSettingsDetails {
    editableInPortal?: boolean;
    visible?: boolean;
    reqDet?: boolean;
    reqNotMet?: boolean;
    reqDataEntry?: boolean;
    display?: string;
}

export interface IAuthContextSettings {
    [key: string]: IAuthContextSettingsDetails;
}

@autoinject()
export abstract class AuthContext {

    ////****************** Fields and Properties **************************************////
    public auth: AuthWithLocalState = { _state: {} };
    public latestEagleTransaction: UM.EagleTransaction = {};
    public cwqiEpisodeGuidelines: CwqiEpisodeGuideline[] = [];
    public apiSettings: IApiSettings = {};
    public apiSettingsSet: boolean = false;
    public authSubClass: UM.AuthSubClass = {};

    protected allowNullDateOfService: boolean = false;

    public authRequiredForDeterminationMet: boolean = false;
    public authRequiredForDataEntryMet: boolean = false;

    public missingRequiredForDetermination: string[] = [];
    public missingRequiredForDataEntry: string[] = [];

    private readonly defaultSettings: IAuthContextSettings = {
        admissionSource: {
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        careDays: {
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        chiefComplaint: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        diagnoses: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        drugApprovedCharges: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        drugRequestedCharges: {
            editableInPortal: false,
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        drugRequestedDays: {
            editableInPortal: false,
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        drugRequestedQuantity: {
            editableInPortal: false,
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        drugs: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        drugsAuthDate: {
            display: '',
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        drugsDate: {
            reqNotMet: false,
        },
        drugsStatus: {
            reqNotMet: false,
        },
        drugsStatusReason: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false
        },
        drugsExpDate: {
            editableInPortal: false,
            display: '',
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        healthcareServiceType: {
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        placeOfService: {
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        priority: {
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        providerAttending: {
            display: '',
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        providerRequested: {
            display: '',
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        providerRequesting: {
            display: '',
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        providerServicing: {
            display: '',
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        requestedDateTime: {
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        serviceApprovedCharges: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        serviceRequestedCharges: {
            editableInPortal: false,
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        serviceRequestedQuantity: {
            editableInPortal: false,
        },
        services: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        servicesAuthDate: {
            display: '',
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        servicesDental: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false
        },
        serviceDentalRequestedQuantity: {
            editableInPortal: false
        },
        servicesDentalAuthDate: {
            display: '',
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        servicesDentalStatus: {
            reqNotMet: false,
        },
        servicesDentalStatusReason: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false
        },
        servicesDentalExpDate: {
            editableInPortal: false,
            display: '',
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false,
        },
        servicesStatus: {
            reqNotMet: false,
        },
        servicesStatusReason: {
            reqDataEntry: false,
            reqDet: false,
            reqNotMet: false,
            visible: false
        },
        servicesExpDate: {
            editableInPortal: false,
            display: '',
            reqDataEntry: false,
            reqDet: false,
            visible: false,
        },
        servicesDentalModifier: {
            visible: false,
        },
        servicesModifier: {
            visible: false,
        }
    }

    public settings: IAuthContextSettings = deepClone(this.defaultSettings);

    //visible settings
    public sourceOfAuthVisible: boolean = false;
    public transportationVisible: boolean = false;

    //required for determination
    public sourceOfAuthReqDet: boolean = false;
    public syncToClaimsSystem: boolean = false;
    public syncToClaimsSystemLineLevel: boolean = false;
    public transportationReqDet: boolean = false;

    //required for data entry
    public sourceOfAuthReqDataEntry: boolean = false;
    public transportationReqDataEntry: boolean = false;

    //service requests
    public serviceRequestAuthStatusId: string | undefined = undefined;
    public serviceRequestSetAuthStatus: boolean = false;
    public serviceRequestRouteToQueue: boolean = false;

    //service correspondence rules
    public serviceCorrespondenceRulesEnabled: boolean = false;

    //correspondence rules
    public correspondenceRulesEnabled: boolean = false;

    //documenation attached
    public documentAttachmentAuthStatusId: string | undefined = undefined;
    public documentAttachmentSetAuthStatus: boolean = false;
    public documentAttachmentRouteToQueue: boolean = false;

    //note attached
    public noteAttachmentAuthStatusId: string | undefined = undefined;
    public noteAttachmentSetAuthStatus: boolean = false;
    public noteAttachmentRouteToQueue: boolean = false;

    //turn around time
    public lineLevelTAT: boolean = false;

    //portal
    protected isProviderPortal: boolean = false;
    public portalAddServices: boolean = false;
    public portalAddDocuments: boolean = false;
    public portalAutoAuth: boolean = false;
    public portalDeterminationRules: boolean = false;
    public portalIQC: boolean = false;

    protected isConfigured: boolean = false;


    ////****************** Lifecycle ***********************************************////
    constructor(
        protected readonly authService: AuthService,
        protected readonly cwqiService: CWQIService,
        protected readonly customFieldService: CustomFieldService,
        protected readonly env: EnvironmentService,
        protected readonly toaster: Toaster,
        protected readonly logger: AppLogger,
        protected readonly events: EventAggregator,
        protected readonly i18n: I18N,
        protected readonly accountContext: AccountContext) {

        this.logger.source = this;
        this.isProviderPortal = this.env.application.code === 'Portal.Providers';
    }


    ////****************** Functions ***********************************************////
    public authIsReadOnly(canEditAuths: boolean, canEditClosedAuths?: boolean): boolean {
        if(!this.auth) { this.logger.warn('Context has no auth loaded'); }
        const readonly = authIsReadOnly(this.auth, this.env.user.id, canEditAuths, canEditClosedAuths);
        this.logger.debug(`authIsReadonly: ${readonly}`);
        return readonly ?? true;
    }

    public async configure(authId: string): Promise<void> {
        this.isConfigured = true;
        await this.refreshAuthSettings({ id: authId });
    }

    public async refreshAuthSettings(auth?: UM.Auth): Promise<void> {
        const authId = auth?.id || this.auth?.id || null;

        if(!authId) {
            this.logger.error("Missing required object: auth");
            throw new Error('Missing required object auth');
        }

        const [loadedAuth, settings, guidelines] = await Promise.all([
            this.loadAuth(authId),
            this.loadSettings(authId),
            this.loadCWQIEpisodeGuidelines(authId)
        ]);

        this.auth = loadedAuth;
        if(this.auth.chiefComplaint && !this.auth.chiefComplaint.isActive) this.auth.chiefComplaint = null;
        this.apiSettings = settings;

        if(this.cwqiEpisodeGuidelines.length > 0 || guidelines.length > 0) {
            this.cwqiEpisodeGuidelines = guidelines;
            this.events.publish(new CwqiReloadedGuidelinesEvent());
        }

        this.apiSettingsSet = this.initializeSettings();

        //load account context and check permission before validation
        await this.accountContext.load();
        this.allowNullDateOfService = this.accountContext.hasPermission('UM.Auth.Duty.InsertNullDOS');

        this.missingRequiredForDataEntry = this.checkRequiredForDataEntry();
        this.missingRequiredForDetermination = [...this.missingRequiredForDataEntry, ...this.checkRequiredForDetermination()].filter(function(elem, index, self) {
            return index === self.indexOf(elem);
        });

        this.authRequiredForDataEntryMet = this.missingRequiredForDataEntry.length === 0;
        this.authRequiredForDeterminationMet = this.missingRequiredForDetermination.length === 0;

        // we want to get the contracted status off of the providers array | JP
        getProviderContractStatus(this.auth, this.auth.provider?.attending);
        getProviderContractStatus(this.auth, this.auth.provider?.requested);
        getProviderContractStatus(this.auth, this.auth.provider?.requesting);
        getProviderContractStatus(this.auth, this.auth.provider?.servicing);
    }

    public initializeSettings(): boolean {

        if(this.apiSettings) {
            this.settings = this.getSettings(this.apiSettings);

            //source of auth
            this.sourceOfAuthVisible = !!getApiSetting(this.apiSettings, 'source.$isVisible');
            this.sourceOfAuthReqDet = !!getApiSetting(this.apiSettings, 'source.$isRequiredForDetermination');
            this.sourceOfAuthReqDataEntry = !!getApiSetting(this.apiSettings, 'source.$isRequired');

            //sync to claims system
            this.syncToClaimsSystem = !!getApiSetting(this.apiSettings, 'syncToClaimsSystem.$isEnabled') && !!getApiSetting(this.apiSettings, 'status.claimsSystem.$isVisible');
            this.syncToClaimsSystemLineLevel = !!getApiSetting(this.apiSettings, 'syncToClaimsSystemLineLevel.$isEnabled') && !!getApiSetting(this.apiSettings, 'status.claimsSystem.$isVisible');

            //service correspondence rules
            this.serviceCorrespondenceRulesEnabled = !!getApiSetting(this.apiSettings, 'serviceCorrespondenceRules.$isEnabled');

            //correspondence rules
            this.correspondenceRulesEnabled = !!getApiSetting(this.apiSettings, 'correspondenceRules.$isEnabled');

            //transportation
            this.transportationVisible = !!getApiSetting(this.apiSettings, 'transportation.$isVisible');
            this.transportationReqDet = !!getApiSetting(this.apiSettings, 'transportation.$isRequiredForDetermination');
            this.transportationReqDataEntry = !!getApiSetting(this.apiSettings, 'transportation.$isRequired');

            //service requests
            this.serviceRequestAuthStatusId = getApiSetting(this.apiSettings, 'serviceRequestAuthStatus.$id')?.toString();
            this.serviceRequestSetAuthStatus = !!getApiSetting(this.apiSettings, 'serviceRequestSetAuthStatus.$isEnabled');
            this.serviceRequestRouteToQueue = !!getApiSetting(this.apiSettings, 'serviceRequestRouteToQueue.$isEnabled');

            //document attached
            this.documentAttachmentAuthStatusId = getApiSetting(this.apiSettings, 'documentAttachmentAuthStatus.$id')?.toString();
            this.documentAttachmentSetAuthStatus = !!getApiSetting(this.apiSettings, 'documentAttachmentSetAuthStatus.$isEnabled');
            this.documentAttachmentRouteToQueue = !!getApiSetting(this.apiSettings, 'documentAttachmentRouteToQueue.$isEnabled');

            //note attached
            this.noteAttachmentAuthStatusId = getApiSetting(this.apiSettings, 'noteAttachmentAuthStatus.$id')?.toString();
            this.noteAttachmentSetAuthStatus = !!getApiSetting(this.apiSettings, 'noteAttachmentSetAuthStatus.$isEnabled');
            this.noteAttachmentRouteToQueue = !!getApiSetting(this.apiSettings, 'noteAttachmentRouteToQueue.$isEnabled');

            //turn around time
            this.lineLevelTAT = !!getApiSetting(this.apiSettings, 'lineLevelTAT.$isEnabled');

            //portal
            this.portalAddServices = !!getApiSetting(this.apiSettings, 'portalAddServices.$isEnabled');
            this.portalAddDocuments = !!getApiSetting(this.apiSettings, 'portalAddDocuments.$isEnabled');
            this.portalAutoAuth = !!getApiSetting(this.apiSettings, 'portalMedicalReviewAutoAuth.$isEnabled');
            this.portalDeterminationRules = !!getApiSetting(this.apiSettings, 'portalMedicalReviewDeterminationRules.$isEnabled');
            this.portalIQC = !!getApiSetting(this.apiSettings, 'portalMedicalReviewIQC.$isEnabled');
            return true;
        }

        return false;
    }

    public getSettings(apiSettings: IApiSettings): IAuthContextSettings {
        const settings = deepClone(this.defaultSettings);
        //care days
        if(apiSettings.admission) {
            settings.careDays.visible = !!getApiSetting(apiSettings, 'admission.preServiceDays.$isVisible');
            settings.careDays.reqDataEntry = !!getApiSetting(apiSettings, 'admission.preServiceDays.$isRequired');
            settings.careDays.reqDet = !!getApiSetting(apiSettings, 'admission.preServiceDays.$isRequiredForDetermination');
        }

        //chief complaint
        settings.chiefComplaint.visible = !!getApiSetting(apiSettings, 'chiefComplaint.$isVisible');
        settings.chiefComplaint.reqDet = !!getApiSetting(apiSettings, 'chiefComplaint.$isRequiredForDetermination');
        settings.chiefComplaint.reqDataEntry = !!getApiSetting(apiSettings, 'chiefComplaint.$isRequired');
        settings.chiefComplaint.reqNotMet = false;

        //diagnoses
        settings.diagnoses.visible = !!getApiSetting(apiSettings, 'diagnoses.$isVisible');
        settings.diagnoses.reqDet = !!getApiSetting(apiSettings, 'diagnoses.$isRequiredForDetermination');
        settings.diagnoses.reqDataEntry = !!getApiSetting(apiSettings, 'diagnoses.$isRequired');
        settings.diagnoses.reqNotMet = false;

        //drugs/medications
        settings.drugs.visible = !!getApiSetting(apiSettings, 'medications.$isVisible');
        settings.drugs.reqDet = !!getApiSetting(apiSettings, 'medications.$isRequiredForDetermination');
        settings.drugs.reqDataEntry = !!getApiSetting(apiSettings, 'medications.$isRequired');
        settings.drugs.reqNotMet = false;

        //drugs date
        settings.drugsDate.reqNotMet = false;

        //drugs/medications authorized date
        settings.drugsAuthDate.display = (getApiSetting(apiSettings, 'medications.authorizedDate.$display') as string);
        settings.drugsAuthDate.visible = !!getApiSetting(apiSettings, 'medications.authorizedDate.$isVisible');
        settings.drugsAuthDate.reqDataEntry = !!getApiSetting(apiSettings, 'medications.authorizedDate.$isRequired');
        settings.drugsAuthDate.reqDet = !!getApiSetting(apiSettings, 'medications.authorizedDate.$isRequiredForDetermination');

        //drugs/medications expiration date
        settings.drugsExpDate.display = (getApiSetting(apiSettings, 'medications.expirationDate.$display') as string);
        settings.drugsExpDate.visible = !!getApiSetting(apiSettings, 'medications.expirationDate.$isVisible');
        settings.drugsExpDate.reqDataEntry = !!getApiSetting(apiSettings, 'medications.expirationDate.$isRequired');
        settings.drugsExpDate.reqDet = !!getApiSetting(apiSettings, 'medications.expirationDate.$isRequiredForDetermination');
        settings.drugsExpDate.editableInPortal = !!getApiSetting(apiSettings, 'medications.expirationDate.$isEditable');

        //drug requested charges
        settings.drugRequestedCharges.visible = !!getApiSetting(apiSettings, 'medications.charge.requested.$isVisible');
        settings.drugRequestedCharges.reqDet = !!getApiSetting(apiSettings, 'medications.charge.requested.$isRequiredForDetermination');
        settings.drugRequestedCharges.reqDataEntry = !!getApiSetting(apiSettings, 'medications.charge.requested.$isRequired');
        settings.drugRequestedCharges.reqNotMet = false;
        settings.drugRequestedCharges.editableInPortal = !!getApiSetting(apiSettings, 'medications.charge.requested.$isEditable');

        //drug requested days
        settings.drugRequestedDays.visible = !!getApiSetting(apiSettings, 'medications.days.requested.$isVisible');
        settings.drugRequestedDays.reqDet = !!getApiSetting(apiSettings, 'medications.days.requested.$isRequiredForDetermination');
        settings.drugRequestedDays.reqDataEntry = !!getApiSetting(apiSettings, 'medications.days.requested.$isRequired');
        settings.drugRequestedDays.reqNotMet = false;
        settings.drugRequestedDays.editableInPortal = !!getApiSetting(apiSettings, 'medications.days.requested.$isEditable');

        //drug requested quantity
        settings.drugRequestedQuantity.visible = !!getApiSetting(apiSettings, 'medications.quantity.requested.$isVisible');
        settings.drugRequestedQuantity.reqDet = !!getApiSetting(apiSettings, 'medications.quantity.requested.$isRequiredForDetermination');
        settings.drugRequestedQuantity.reqDataEntry = !!getApiSetting(apiSettings, 'medications.quantity.requested.$isRequired');
        settings.drugRequestedQuantity.reqNotMet = false;
        settings.drugRequestedQuantity.editableInPortal = !!getApiSetting(apiSettings, 'medications.quantity.requested.$isEditable');

        //drug approved charges
        settings.drugApprovedCharges.visible = !!getApiSetting(apiSettings, 'medications.charge.approved.$isVisible');
        settings.drugApprovedCharges.reqDet = !!getApiSetting(apiSettings, 'medications.charge.approved.$isRequiredForDetermination');
        settings.drugApprovedCharges.reqDataEntry = !!getApiSetting(apiSettings, 'medications.charge.approved.$isRequired');

        //drug expiration date
        settings.drugsExpDate.editableInPortal = !!getApiSetting(apiSettings, 'medications.expirationDate.$isEditable');

        //drug requested days
        settings.drugRequestedDays.editableInPortal = !!getApiSetting(apiSettings, 'medications.days.requested.$isEditable');

        //drugs/medications status reason
        settings.drugsStatusReason.visible = !!getApiSetting(apiSettings, 'medications.status.reason.$isVisible');
        settings.drugsStatusReason.reqDataEntry = !!getApiSetting(apiSettings, 'medications.status.reason.$isRequired');
        settings.drugsStatusReason.reqDet = !!getApiSetting(apiSettings, 'medications.status.reason.$isRequiredForDetermination');

        //healthcare service type
        settings.healthcareServiceType.visible = !!getApiSetting(apiSettings, 'healthcareServiceType.$isVisible');

        //place of service
        settings.placeOfService.visible = !!getApiSetting(apiSettings, 'placeOfService.$isVisible');

        //priority
        settings.priority.visible = !!getApiSetting(apiSettings, 'priority.$isVisible');
        settings.priority.reqDet = !!getApiSetting(apiSettings, 'priority.$isRequiredForDetermination');
        settings.priority.reqDataEntry = !!getApiSetting(apiSettings, 'priority.$isRequired');

        //source of admission
        settings.admissionSource.visible = !!getApiSetting(apiSettings, 'admission.source.$isVisible');
        settings.admissionSource.reqDataEntry = !!getApiSetting(apiSettings, 'admission.source.$isRequired');
        settings.admissionSource.reqDet = !!getApiSetting(apiSettings, 'admission.source.$isRequiredForDetermination');

        //healthcare service type
        settings.healthcareServiceType.visible = !!getApiSetting(apiSettings, 'healthcareServiceType.$isVisible');
        settings.healthcareServiceType.reqDet = !!getApiSetting(apiSettings, 'healthcareServiceType.$isRequiredForDetermination');
        settings.healthcareServiceType.reqDataEntry = !!getApiSetting(apiSettings, 'healthcareServiceType.$isRequired');

        //Place of Service
        settings.placeOfService.visible = !!getApiSetting(apiSettings, 'placeOfService.$isVisible');
        settings.placeOfService.reqDet = !!getApiSetting(apiSettings, 'placeOfService.$isRequiredForDetermination');
        settings.placeOfService.reqDataEntry = !!getApiSetting(apiSettings, 'placeOfService.$isRequired');

        //providers
        settings.providerAttending.visible = !!getApiSetting(apiSettings, 'provider.attending.$isVisible');
        settings.providerAttending.reqDataEntry = !!getApiSetting(apiSettings, 'provider.attending.$isRequired');
        settings.providerAttending.display = (getApiSetting(apiSettings, 'provider.attending.$display') || '') as string;
        settings.providerAttending.reqNotMet = false;

        settings.providerRequested.visible = !!getApiSetting(apiSettings, 'provider.requested.$isVisible');
        settings.providerRequested.reqDataEntry = !!getApiSetting(apiSettings, 'provider.requested.$isRequired');
        settings.providerRequested.display = (getApiSetting(apiSettings, 'provider.requested.$display') || '') as string;
        settings.providerRequested.reqNotMet = false;

        settings.providerRequesting.visible = !!getApiSetting(apiSettings, 'provider.requesting.$isVisible');
        settings.providerRequesting.reqDataEntry = !!getApiSetting(apiSettings, 'provider.requesting.$isRequired');
        settings.providerRequesting.display = (getApiSetting(apiSettings, 'provider.requesting.$display') || '') as string;
        settings.providerRequesting.reqNotMet = false;

        settings.providerServicing.visible = !!getApiSetting(apiSettings, 'provider.servicing.$isVisible');
        settings.providerServicing.reqDataEntry = !!getApiSetting(apiSettings, 'provider.servicing.$isRequired');
        settings.providerServicing.display = (getApiSetting(apiSettings, 'provider.servicing.$display') || '') as string;
        settings.providerServicing.reqNotMet = false;

        //requested date time
        settings.requestedDateTime.visible = !!getApiSetting(apiSettings, 'requestedDateTime.$isVisible');
        settings.requestedDateTime.reqDet = !!getApiSetting(apiSettings, 'requestedDateTime.$isRequiredForDetermination');
        settings.requestedDateTime.reqDataEntry = !!getApiSetting(apiSettings, 'requestedDateTime.$isRequired');

        //services
        settings.services.visible = !!getApiSetting(apiSettings, 'services.$isVisible');
        settings.services.reqDet = !!getApiSetting(apiSettings, 'services.$isRequiredForDetermination');
        settings.services.reqDataEntry = !!getApiSetting(apiSettings, 'services.$isRequired');
        settings.services.reqNotMet = false;

        //services authorized date
        settings.servicesAuthDate.display = (getApiSetting(apiSettings, 'services.authorizedDate.$display') as string);
        settings.servicesAuthDate.visible = !!getApiSetting(apiSettings, 'services.authorizedDate.$isVisible');
        settings.servicesAuthDate.reqDataEntry = !!getApiSetting(apiSettings, 'services.authorizedDate.$isRequired');
        settings.servicesAuthDate.reqDet = !!getApiSetting(apiSettings, 'services.authorizedDate.$isRequiredForDetermination');
        settings.servicesAuthDate.reqNotMet = false;

        //services expiration date
        settings.servicesExpDate.display = (getApiSetting(apiSettings, 'services.expirationDate.$display') as string);
        settings.servicesExpDate.editableInPortal = !!getApiSetting(apiSettings, 'services.expirationDate.$isEditable');
        settings.servicesExpDate.reqDataEntry = !!getApiSetting(apiSettings, 'services.expirationDate.$isRequired');
        settings.servicesExpDate.reqDet = !!getApiSetting(apiSettings, 'services.expirationDate.$isRequiredForDetermination');
        settings.servicesExpDate.visible = !!getApiSetting(apiSettings, 'services.expirationDate.$isVisible');

        //services modifiers
        settings.servicesModifier.visible = !!getApiSetting(apiSettings, 'services.modifiers.$isVisible');

        //service requested charges
        settings.serviceRequestedCharges.editableInPortal = !!getApiSetting(apiSettings, 'services.charge.requested.$isEditable');
        settings.serviceRequestedCharges.reqDataEntry = !!getApiSetting(apiSettings, 'services.charge.requested.$isRequired');
        settings.serviceRequestedCharges.reqDet = !!getApiSetting(apiSettings, 'services.charge.requested.$isRequiredForDetermination');
        settings.serviceRequestedCharges.reqNotMet = false;
        settings.serviceRequestedCharges.visible = !!getApiSetting(apiSettings, 'services.charge.requested.$isVisible');

        //service requested quantity
        settings.serviceRequestedQuantity.editableInPortal = !!getApiSetting(apiSettings, `services.quantity.requested.$isEditable`);

        //service approved charges
        settings.serviceApprovedCharges.visible = !!getApiSetting(apiSettings, 'services.charge.approved.$isVisible');
        settings.serviceApprovedCharges.reqDet = !!getApiSetting(apiSettings, 'services.charge.approved.$isRequiredForDetermination');
        settings.serviceApprovedCharges.reqDataEntry = !!getApiSetting(apiSettings, 'services.charge.approved.$isRequired');

        //services status reason
        settings.servicesStatusReason.visible = !!getApiSetting(apiSettings, 'services.status.reason.$isVisible');
        settings.servicesStatusReason.reqDataEntry = !!getApiSetting(apiSettings, 'services.status.reason.$isRequired');
        settings.servicesStatusReason.reqDet = !!getApiSetting(apiSettings, 'services.status.reason.$isRequiredForDetermination');

        //dental services
        settings.servicesDental.visible = !!getApiSetting(apiSettings, 'services.dental.$isVisible');
        settings.servicesDental.reqDet = !!getApiSetting(apiSettings, 'services.dental.$isRequiredForDetermination');
        settings.servicesDental.reqDataEntry = !!getApiSetting(apiSettings, 'services.dental.$isRequired');
        settings.servicesDental.reqNotMet = false;

        //dental services authorized date
        settings.servicesDentalAuthDate.display = (getApiSetting(apiSettings, 'services.dental.authorizedDate.$display') as string);
        settings.servicesDentalAuthDate.visible = !!getApiSetting(apiSettings, 'services.dental.authorizedDate.$isVisible');
        settings.servicesDentalAuthDate.reqDataEntry = !!getApiSetting(apiSettings, 'services.dental.authorizedDate.$isRequired');
        settings.servicesDentalAuthDate.reqDet = !!getApiSetting(apiSettings, 'services.dental.authorizedDate.$isRequiredForDetermination');
        settings.servicesDentalAuthDate.reqNotMet = false;

        //dental services expiration date
        settings.servicesDentalExpDate.display = (getApiSetting(apiSettings, 'services.dental.expirationDate.$display') as string);
        settings.servicesDentalExpDate.editableInPortal = !!getApiSetting(apiSettings, 'services.dental.expirationDate.$isEditable');
        settings.servicesDentalExpDate.reqDataEntry = !!getApiSetting(apiSettings, 'services.dental.expirationDate.$isRequired');
        settings.servicesDentalExpDate.reqDet = !!getApiSetting(apiSettings, 'services.dental.expirationDate.$isRequiredForDetermination');
        settings.servicesDentalExpDate.visible = !!getApiSetting(apiSettings, 'services.dental.expirationDate.$isVisible');

        //dental services requested quantity
        settings.serviceDentalRequestedQuantity.editableInPortal = !!getApiSetting(apiSettings, 'services.dental.quantity.requested.$isEditable');

        //dental services modifiers
        settings.servicesDentalModifier.visible = !!getApiSetting(apiSettings, 'services.dental.modifiers.$isVisible');

        //dental services status reason
        settings.servicesDentalStatusReason.visible = !!getApiSetting(apiSettings, 'services.dental.status.reason.$isVisible');
        settings.servicesDentalStatusReason.reqDataEntry = !!getApiSetting(apiSettings, 'services.dental.status.reason.$isRequired');
        settings.servicesDentalStatusReason.reqDet = !!getApiSetting(apiSettings, 'services.dental.status.reason.$isRequiredForDetermination');

        return settings;
    }

    protected abstract authDataComplete(authId: string): Promise<boolean>;

    protected abstract checkRequiredForDataEntry(auth?: UM.Auth): string[];

    protected abstract checkRequiredForDetermination(auth?: UM.Auth): string[];

    public async triggerCorrespondenceRules() {
        if(!this.auth.id || !this.correspondenceRulesEnabled) return;

        const correspondence = await this.triggerCorrespondence(this.auth.id);

        if(correspondence.isSuccessful) {
            this.events.publish(new AuthCorrespondenceEvent());
            this.toaster.info().message(this.i18n.tr('ui:labels.ran', { what: '$t(um:correspondenceRules.$)' })).show();
        }

    }

    public async triggerServiceCorrespondenceRules(services: UM.AuthService[]) {
        if(!this.auth.id) { return; }

        const correspondence = await this.triggerServiceCorrespondence(this.auth.id, services);

        if(correspondence.isSuccessful) {
            this.events.publish(new AuthCorrespondenceEvent());
            this.toaster.info().message(this.i18n.tr('ui:labels.ran', { what: '$t(um:correspondenceRules.$)' })).show();
        }
    }

    public async triggerDeterminationRules(): Promise<void> {

        if(!this.auth.id) { return; }

        try {
            const result = await this.autoDetermination(this.auth.id);

            if(result.isSuccessful) {
                this.toaster.info().message(this.i18n.tr('ui:labels.ran', { what: this.i18n.tr('um:determinationRules.$') })).show();
                this.events.publish(new AuthAutoDeterminationEvent());
            }
            else {
                this.toaster.error().message(this.i18n.tr('ui:messages.events.runFailed', { what: this.i18n.tr('um:determinationRules.$') })).show();
            }
        }
        catch(error) {
            this.logger.error('Determination Rule run failed', error);
            this.toaster.error().message(this.i18n.tr('ui:messages.events.runFailed', { what: this.i18n.tr('um:determinationRules.$') })).show();
        }

    }

    public async setAuthStatus(authId: string | null = null, applicationEventCode: string | null = null): Promise<void> {
        if(!authId || !applicationEventCode) return;

        try {
            var result;

            if(applicationEventCode == 'PP.Auth.Service.Added' && this.serviceRequestSetAuthStatus) {
                if(!this.serviceRequestAuthStatusId) return;
                result = await this.saveAuthStatus(authId, this.serviceRequestAuthStatusId, applicationEventCode);
            }

            if(applicationEventCode == 'PP.Auth.Document.Attached' && this.documentAttachmentSetAuthStatus) {
                if(!this.documentAttachmentAuthStatusId) return;
                result = await this.saveAuthStatus(authId, this.documentAttachmentAuthStatusId, applicationEventCode);
            }

            if(applicationEventCode == 'PP.Auth.Note.Attached' && this.noteAttachmentSetAuthStatus) {
                if(!this.noteAttachmentAuthStatusId) return;
                result = await this.saveAuthStatus(authId, this.noteAttachmentAuthStatusId, applicationEventCode);
            }
        }
        catch(error) {
            throw error;
        }
    }

    public async triggerQueueRoutingRules(authId: string | null = null, applicationEventCode: string | null = null): Promise<void> {
        if(!authId) return;
        if(applicationEventCode == 'PP.Auth.Service.Added' && !this.serviceRequestRouteToQueue) return;
        if(applicationEventCode == 'PP.Auth.Document.Attached' && !this.documentAttachmentRouteToQueue) return;
        if(applicationEventCode == 'PP.Auth.Note.Attached' && !this.noteAttachmentRouteToQueue) return;

        try {
            const result = await this.triggerQueueRouting(authId, applicationEventCode);
        }
        catch(error) {
            throw error;
        }
    }

    public clear(): void {
        this.auth = { _state: {} };
        this.apiSettings = {};
        this.apiSettingsSet = false;
        this.cwqiEpisodeGuidelines = [];
        this.authSubClass = {};
        this.isConfigured = false;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadSettings(authId: string): Promise<IApiSettings> {
        try {
            const response = await this.authService.loadAuthSettings(authId);
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

    @apiCall
    public async loadBasicSettings(): Promise<void> {
        try {
            const response = await this.authService.loadAuthSettings();
            this.apiSettings = response.settings;
            this.apiSettingsSet = this.initializeSettings();
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    public async loadCWQIEpisodeGuidelines(authId: string): Promise<CwqiEpisodeGuideline[]> {
        if(this.isProviderPortal || !this.env.config.integrations.includes('cwqi')) { return []; }

        try {
            const includes: string[] = [
                'Metadata_Modified_ByUser'
            ];

            const fields = [...cwqiGuidelineFields, 'indicationsGuideline']

            const response = await this.cwqiService.loadCwqiEpisodeGuidelines(authId, undefined, includes, fields);
            return response.results ?? [];
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

    protected abstract loadAuth(authId: string): Promise<UM.Auth>;

    @apiCall
    private async triggerCorrespondence(authId: string): Promise<IApiResults> {
        try {
            const response = await this.authService.triggerCorrespondence({ id: authId });
            return response;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

    @apiCall
    private async triggerServiceCorrespondence(authId: string, services: UM.AuthService[]): Promise<IApiResults> {
        try {
            const response = await this.authService.triggerServiceCorrespondence(authId, services);
            return response;
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async autoDetermination(authId: string): Promise<IApiResults> {
        try {
            const response = await this.authService.autoDetermination({ id: authId });
            return response;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }

    @apiCall
    private async triggerQueueRouting(authId: string, applicationEventCode: string | null): Promise<IApiResults> {
        return await this.authService.triggerQueueRouting({ id: authId }, applicationEventCode);
    }

    @apiCall
    private async saveAuthStatus(authId: string, authStatusId: string, applicationEventCode: string): Promise<IApiResults> {
        const saveAuth: UM.Auth = {
            id: authId,
            status: { id: authStatusId }
        }

        return await this.authService.saveAuthStatus(saveAuth, applicationEventCode);
    }

}
